/**
 * PaymentModal — Modal de baixa individual (cenários: Cessão, CPR com dup, CPR clean).
 * Wireframe: settlement/docs/po/wireframe-fluxo-baixa-e-liquidacao.pdf
 * Planilha: settlement/docs/po/baixas-e-liquidacoes-cenarios.xlsx
 *
 * Campos (PO):
 * - Valor recebido (R$)
 * - Meio de pagamento (PIX | Boleto | Novo Boleto)
 * - Conta bancária (Itau | BB | Stark | Daycoval) — quando forma = Financeiro
 * - Tipo liquidação (Total | Parcial | Com desconto)
 * - Fonte pagamento (Sacado | Emitente | Avalista)
 * - Sub-rogação (flag)
 * - Observações
 */
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { registerPayment } from '../services/settlementApi';
import { PaymentMethod, SettlementType, BankAccount } from '../constants/settlement';
import { toast } from 'react-toastify';

interface Props {
    titleDebt: any;
    onClose: () => void;
    onSuccess: () => void;
}

const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value || '0') : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (date: string | null) => {
    if (!date) return '-';
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const PaymentModal: React.FC<Props> = ({ titleDebt, onClose, onSuccess }) => {
    const remaining = parseFloat(titleDebt.amount) - parseFloat(titleDebt.paid_amount || '0');

    const [form, setForm] = useState({
        amount: remaining.toFixed(2),
        payment_method: PaymentMethod.PIX as string,
        settlement_type: SettlementType.TOTAL as string,
        bank_account: BankAccount.ITAU as string,
        payment_source: 'SACADO',
        is_subrogation: false,
        notes: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        const amount = parseFloat(form.amount);
        if (!amount || amount <= 0) {
            toast.error('Informe um valor maior que zero');
            return;
        }
        setSubmitting(true);
        try {
            await registerPayment(titleDebt.id, {
                amount,
                payment_method: form.payment_method,
                settlement_type: form.settlement_type,
                bank_account: form.bank_account,
                payment_source: form.payment_source,
                is_subrogation: form.is_subrogation,
                notes: form.notes,
            });
            toast.success('Baixa registrada com sucesso');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro ao registrar baixa: ${e.message}`);
        }
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Registrar Baixa</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Context */}
                <div className="mb-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div className="grid grid-cols-2 gap-2">
                        <p><span className="text-gray-500">Contrato:</span> <strong>{titleDebt.contract_number}</strong></p>
                        <p><span className="text-gray-500">Parcela:</span> <strong>{titleDebt.document_title || titleDebt.installment_number || '-'}</strong></p>
                        <p><span className="text-gray-500">Valor:</span> <strong>{formatCurrency(titleDebt.amount)}</strong></p>
                        <p><span className="text-gray-500">Vencimento:</span> <strong>{formatDate(titleDebt.due_date)}</strong></p>
                        <p><span className="text-gray-500">Já pago:</span> <strong>{formatCurrency(titleDebt.paid_amount || '0')}</strong></p>
                        <p><span className="text-gray-500">Saldo:</span> <strong className="text-red-600">{formatCurrency(remaining)}</strong></p>
                    </div>
                </div>

                {/* Form */}
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor do Pagamento (R$)</label>
                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de Pagamento</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                                <option value={PaymentMethod.PIX}>PIX</option>
                                <option value={PaymentMethod.TED}>TED</option>
                                <option value={PaymentMethod.BOLETO}>Boleto</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Liquidação</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.settlement_type} onChange={e => setForm({ ...form, settlement_type: e.target.value })}>
                                <option value={SettlementType.TOTAL}>Total</option>
                                <option value={SettlementType.PARCIAL}>Parcial</option>
                                <option value={SettlementType.ANTECIPADO}>Antecipado (desconto)</option>
                                <option value={SettlementType.ATRASO}>Em atraso (com juros)</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conta Bancária</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })}>
                                <option value={BankAccount.ITAU}>Itaú</option>
                                <option value={BankAccount.BB}>Banco do Brasil</option>
                                <option value={BankAccount.STARKBANK}>Stark Bank</option>
                                <option value={BankAccount.DAYCOVAL}>Daycoval</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fonte do Pagamento</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.payment_source} onChange={e => setForm({ ...form, payment_source: e.target.value })}>
                                <option value="SACADO">Sacado</option>
                                <option value="EMITENTE">Emitente</option>
                                <option value="AVALISTA">Avalista</option>
                                <option value="CREDOR">Credor</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="subrogation" className="rounded"
                            checked={form.is_subrogation} onChange={e => setForm({ ...form, is_subrogation: e.target.checked })} />
                        <label htmlFor="subrogation" className="text-sm text-gray-700 dark:text-gray-300">Sub-rogação</label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                            placeholder="Observações opcionais..." />
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                        {submitting ? 'Registrando...' : 'Confirmar Baixa'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
